/*
  Series pack media slider.

  The track is a scroll-snap strip and the arrows, dots and keyboard all move it
  by scrolling — there is no transform position kept alongside the scroll offset,
  so a swipe and a click cannot disagree about which slide is showing. With the
  script absent the same markup is still a scrollable strip of every image, which
  is why the arrows and dots are hidden until the element reports itself ready.

  It also advances on its own at the interval the section sets, which is why the
  paths below care about who moved the slider last: autoplay pauses while a
  visitor is reading a slide, and stops for good once they take the controls.
*/
if (!customElements.get('series-pack-slider')) {
  customElements.define(
    'series-pack-slider',
    class SeriesPackSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-series-pack-track]');
        this.slides = Array.from(this.querySelectorAll('[data-series-pack-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-series-pack-dot]'));
        this.previousButton = this.querySelector('[data-series-pack-previous]');
        this.nextButton = this.querySelector('[data-series-pack-next]');

        if (!this.track || this.slides.length < 2) return;

        this.index = 0;
        this.ticking = false;
        this.timer = null;
        this.onScroll = this.onScroll.bind(this);

        /* An interval of 0 is the merchant turning autoplay off, and a reduced
           motion preference turns it off over their head — a carousel moving on
           its own is the thing that setting is asking us not to do. */
        const interval = parseInt(this.dataset.autoplay, 10);
        const stillness = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.interval = interval > 0 && !stillness.matches ? interval : 0;

        if (this.previousButton) {
          this.previousButton.addEventListener('click', () => {
            this.stop();
            this.select(this.index - 1);
          });
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('click', () => {
            this.stop();
            this.select(this.index + 1);
          });
        }

        this.dots.forEach((dot, index) => {
          dot.addEventListener('click', () => {
            this.stop();
            this.select(index);
          });
        });

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          this.stop();
          this.select(event.key === 'ArrowLeft' ? this.index - 1 : this.index + 1);
        });

        /* A drag on the track is the visitor taking over as much as a click on an
           arrow is, even though it never reaches `select`. */
        this.track.addEventListener('pointerdown', () => this.stop());

        /* Hover and focus only pause — the visitor is reading this slide, not
           steering. A hidden tab pauses for a related reason: nobody is there to
           watch the slides it would otherwise spend. */
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.play());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => this.play());
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.pause();
          else this.play();
        });

        this.classList.add('series-pack__slider--ready');
        this.update();
        this.play();
      }

      /* The element is torn down and rebuilt on every theme editor change, and a
         timer left behind would keep a detached slider ticking. */
      disconnectedCallback() {
        this.pause();
      }

      /* The visibilitychange listener outlives the element, so a slider the
         theme editor has already replaced must not be able to start ticking again. */
      play() {
        if (!this.interval || this.timer || !this.isConnected) return;
        this.timer = setInterval(() => this.advance(), this.interval);
      }

      pause() {
        clearInterval(this.timer);
        this.timer = null;
      }

      /* Stopping is the permanent one: `play` has nothing left to restart. */
      stop() {
        this.pause();
        this.interval = 0;
      }

      /* Rewinding through every slide to reach the first would take longer than
         the interval that started it, so the wrap is a cut rather than a scroll. */
      advance() {
        if (this.index === this.slides.length - 1) {
          this.track.scrollTo({ left: 0, behavior: 'auto' });
        } else {
          this.select(this.index + 1);
        }
      }

      select(index) {
        const target = Math.min(Math.max(index, 0), this.slides.length - 1);
        this.track.scrollTo({ left: this.track.clientWidth * target, behavior: 'smooth' });
      }

      /* Reading the index back off the scroll offset keeps the dots honest about
         a swipe or a trackpad flick, which never go through `select`. */
      onScroll() {
        if (this.ticking) return;
        this.ticking = true;

        requestAnimationFrame(() => {
          this.ticking = false;
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (index === this.index) return;
          this.index = index;
          this.update();
        });
      }

      update() {
        this.dots.forEach((dot, index) => {
          const current = index === this.index;
          dot.classList.toggle('series-pack__dot--current', current);
          dot.setAttribute('aria-current', current ? 'true' : 'false');
        });

        if (this.previousButton) this.previousButton.disabled = this.index === 0;
        if (this.nextButton) this.nextButton.disabled = this.index === this.slides.length - 1;
      }
    }
  );
}
